import { Injectable } from "@angular/core";
import { ConfigService } from "../../../config/config.service";
import { AppStore } from "../../appStore";
import { Store } from "@ngrx/store";

import { Observable, Observer, take } from "rxjs";
import { JwtHelperService } from "@auth0/angular-jwt";
import { CoreConfig } from "../../../config/core-config.type";
import { Router } from "@angular/router";
import { environment } from "../../../../environments/environment";
import { Environments } from "../../../../environments/environments";
import { authLoginSuccess } from "./store/auth.actions";
import { Capacitor } from "@capacitor/core";
import { TokenStorage } from "./token-storage";
import { KeycloakEventTypeLegacy, KeycloakService } from "keycloak-angular";
import { Browser } from "@capacitor/browser";
import {
  GenericOAuth2,
  OAuth2AuthenticateOptions,
} from "@capacitor-community/generic-oauth2";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  get accessToken() {
    return this._tokenStorage.read().token ?? "";
  }

  get refreshToken() {
    return this._tokenStorage.read().refreshToken ?? null;
  }

  get idToken() {
    return this._tokenStorage.read().idToken ?? null;
  }

  get user() {
    return {
      ...new JwtHelperService().decodeToken(
        this.accessToken ? this.accessToken : ""
      ),
      ...{ token: this.accessToken },
    };
  }

  get eMail() {
    return this.user.upn;
  }

  private _config: CoreConfig;
  private _tokenStorage: TokenStorage;
  private _authenticationRunning = false;
  private _initialRequest = true;
  private _oauth2Options: OAuth2AuthenticateOptions;

  /*
   * Android's OAuth custom tab regularly reports USER_CANCELLED for a dismissal the user never
   * triggered, which dropped them on the login-error page reading "Vom Benutzer abgebrochen" -
   * the reason Play Store review rejected the build. Repeating the flow succeeds nearly every
   * time, so an attempt that fails for any reason is retried before the error page is shown.
   */
  private static readonly MAX_AUTHENTICATION_ATTEMPTS = 2;

  /*
   * Reopening the custom tab in the same tick it closed tends to fail the same way, so the retry
   * waits for the dismissal to settle first.
   */
  private static readonly AUTHENTICATION_RETRY_DELAY_MS = 400;

  get oauth2Options(): OAuth2AuthenticateOptions {
    return this._oauth2Options;
  }

  get initialRequest(): boolean {
    return this._initialRequest;
  }

  constructor(
    private _configService: ConfigService,
    private _store: Store<AppStore>,
    private _router: Router,
    private _keycloakService: KeycloakService
  ) {
    this._config = this._configService.config;
    this._tokenStorage = new TokenStorage(this._config);

    this._oauth2Options = {
      authorizationBaseUrl:
        this._config.auth.url + this._config.auth.tokenEndpoint,
      scope: "openid profile",
      accessTokenEndpoint:
        this._config.auth.url + this._config.auth.accessTokenEndpoint,
      pkceEnabled: true,
      appId: this._config.auth.clientId,
      logsEnabled:
        environment.deployment === Environments.develop ||
        environment.deployment === Environments.staging,
      web: {
        appId: this._config.auth.clientId,
        responseType: "code",
        pkceEnabled: true,
        redirectUrl: this._config.auth.redirectUri,
        windowOptions: "height=600,left=0,top=0",
      },
      ios: {
        appId: this._config.auth.clientId,
        responseType: "code",
        redirectUrl: this._config.auth.redirectUriiOS,
        pkceEnabled: true,
      },
      android: {
        appId: this._config.auth.clientId,
        responseType: "code",
        redirectUrl: this._config.auth.redirectUriAndroid,
        pkceEnabled: true,
      },
    };

    if (Capacitor.getPlatform() === "web") {
      if (this._keycloakService.isLoggedIn()) {
        this._keycloakService.getToken().then((token: string) => {
          this._store.dispatch(authLoginSuccess({ token: token }));
          this.persistKeycloakTokens();
        });
      }

      // ⚠ keycloakEvents$ emits the numeric KeycloakEventTypeLegacy, not the string
      // KeycloakEventType - comparing against the latter silently never matches.
      this._keycloakService.keycloakEvents$.subscribe({
        next: (e) => {
          switch (e.type) {
            case KeycloakEventTypeLegacy.OnAuthSuccess:
            case KeycloakEventTypeLegacy.OnAuthRefreshSuccess:
              this.persistKeycloakTokens();
              break;
            case KeycloakEventTypeLegacy.OnTokenExpired:
              this._keycloakService.updateToken(20);
              break;
            case KeycloakEventTypeLegacy.OnAuthRefreshError:
            case KeycloakEventTypeLegacy.OnAuthLogout:
              // Stale tokens would send the next init down the refresh path just to
              // have it rejected, instead of straight to check-sso.
              this.removeToken();
              break;
          }
        },
      });
    } else {
      this.isAuthenticated()
        .pipe(take(1))
        .subscribe((res) => {
          if (res) {
            this._store.dispatch(authLoginSuccess({ token: this.accessToken }));
          }
          this._initialRequest = true;
        });
    }
  }

  public authenticationObservable: Observable<boolean>;
  public authenticationObservableCompleted = true;

  public isAuthenticated(setInitial = false) {
    if (!this.authenticationObservableCompleted) {
      return this.authenticationObservable;
    }

    this.authenticationObservable = new Observable((obs) => {
      if (setInitial) this._initialRequest = false;

      //if access token is valid then everything is fine
      if (this.accessToken && !this.isAuthTokenExpired()) {
        obs.next(true);
        this.authenticationObservableCompleted = true;
        obs.complete();
      } else if (this.refreshToken && !this.isRefreshTokenExpired()) {
        if (Capacitor.getPlatform() === "web") {
          this._keycloakService.updateToken(20).then((res: boolean) => {
            obs.next(res);
            this.authenticationObservableCompleted = true;
            obs.complete();
          });
        } else {
          GenericOAuth2.refreshToken({
            refreshToken: this.refreshToken,
            appId: this._oauth2Options.appId ? this._oauth2Options.appId : "",
            accessTokenEndpoint: this._oauth2Options.accessTokenEndpoint
              ? this._oauth2Options.accessTokenEndpoint
              : "",
          })
            .then((response) => {
              this.saveToken(
                response.access_token,
                response.refresh_token,
                response.id_token ? response.id_token : null
              );
              obs.next(true);
              this.authenticationObservableCompleted = true;
              obs.complete();
            })
            .catch((err) => {
              if (this.accessToken && !this.isAuthTokenExpired()) {
                //there is a chance that the token was refreshed in the meantime
                console.log("Token was refreshed in the meantime");
                obs.next(true);
                this.authenticationObservableCompleted = true;
                obs.complete();
              } else {
                obs.next(false);
                this.authenticationObservableCompleted = true;
                obs.complete();
                this.removeToken();
                window.location.reload();
              }
            });
        }
      } else {
        obs.next(false);
        this.authenticationObservableCompleted = true;
        obs.complete();
      }
    });

    return this.authenticationObservable;
  }

  public isAuthTokenExpired() {
    if (!this.accessToken) return true;

    return new JwtHelperService().isTokenExpired(this.accessToken);
  }

  public isRefreshTokenExpired() {
    return new JwtHelperService().isTokenExpired(this.refreshToken);
  }

  public authenticate(obs: Observer<boolean>) {
    if (this._authenticationRunning) {
      // Return instead of falling through: starting a second OAuth flow on top of the running
      // one leaves two custom tabs fighting over the same redirect, which is itself one of the
      // ways the spurious USER_CANCELLED is produced. The in-flight flow still completes and
      // dispatches authLoginSuccess, so the user is not left logged out.
      obs.next(false);
      obs.complete();
      return;
    }

    this._authenticationRunning = true;

    if (Capacitor.getPlatform() === "web") {
      // Web authenticates through Keycloak, not the plugin; nothing to run here.
      this._authenticationRunning = false;
      return;
    }

    this.runAuthentication(obs, 1);
  }

  /*
   * A single pass at the native OAuth flow. `obs` is deliberately left open while retries are
   * still available, so however many attempts it takes, callers observe exactly one result.
   */
  private runAuthentication(obs: Observer<boolean>, attempt: number) {
    GenericOAuth2.authenticate(this.oauth2Options)
      .then((response) => {
        if (
          response &&
          response.access_token_response &&
          response.access_token_response.access_token
        ) {
          this.saveToken(
            response.access_token_response.access_token,
            response.access_token_response.refresh_token,
            response.access_token_response.id_token
              ? response.access_token_response.id_token
              : null
          );
          this._authenticationRunning = false;
          obs.next(true);
          obs.complete();
          this._store.dispatch(authLoginSuccess({ token: this.accessToken }));
        } else {
          console.error("OAuth invalid");
          this.retryOrFailAuthentication(obs, attempt, "INVALID_RESPONSE");
        }
      })
      .catch((reason) => {
        console.error(reason.toString());
        this.retryOrFailAuthentication(
          obs,
          attempt,
          reason.toString().replace("Error: ", "")
        );
      });
  }

  /*
   * Retries the flow while attempts remain, and only gives up - completing `obs` and routing to
   * the error page - once they are spent.
   */
  private retryOrFailAuthentication(
    obs: Observer<boolean>,
    attempt: number,
    error: string
  ) {
    if (attempt < AuthService.MAX_AUTHENTICATION_ATTEMPTS) {
      console.warn(
        `Authentication attempt ${attempt} failed (${error}), retrying`
      );

      setTimeout(
        () => this.runAuthentication(obs, attempt + 1),
        AuthService.AUTHENTICATION_RETRY_DELAY_MS
      );
      return;
    }

    this._authenticationRunning = false;
    obs.next(false);
    obs.complete();

    this._router.navigateByUrl("/login-error?error=" + error);
  }

  public saveRefreshTokenResponse(refreshTokenResponse: {
    access_token: string;
    refresh_token: string;
  }) {
    if (refreshTokenResponse.access_token) {
      this.saveToken(
        refreshTokenResponse.access_token,
        refreshTokenResponse.refresh_token
      );
    } else {
      console.error("OAuth Refresh invalid");
    }
  }

  private saveToken(
    access_token: string,
    refresh_token: string | null = null,
    id_token: string | null = null
  ) {
    this._tokenStorage.write({
      token: access_token,
      refreshToken: refresh_token ?? undefined,
      idToken: id_token ?? undefined,
    });
  }

  /**
   * Copies the in-memory Keycloak tokens to localStorage so the next init can restore
   * the session without a round trip to the IDM.
   */
  private persistKeycloakTokens() {
    const keycloak = this._keycloakService.getKeycloakInstance();

    // ⚠ Without a refresh token keycloak.init() ignores the pair and falls back to
    // check-sso, so a lone access token is not worth storing.
    if (!keycloak?.token || !keycloak.refreshToken) {
      return;
    }

    this.saveToken(
      keycloak.token,
      keycloak.refreshToken,
      keycloak.idToken ?? null
    );
  }

  public logout(): void {
    if (Capacitor.getPlatform() === "web") {
      this._keycloakService
        .logout(this._config.auth.redirectUri + "/logout")
        .then(() => {
          this.removeToken();
        })
        .catch((err) => {
          this.removeToken();
        });
    } else {
      GenericOAuth2.logout(this._oauth2Options)
        .then((res) => {
          this.customLogout();
        })
        .catch((err) => {
          this.customLogout();
        });
    }
  }

  private customLogout() {
    let encodedRedirectUri = encodeURIComponent(
      this._config.auth.redirectUri + "/logout"
    );
    // when ios use redirecturiios
    if (Capacitor.getPlatform() === "ios") {
      encodedRedirectUri = encodeURIComponent(this._config.auth.redirectUriiOS);
    } else if (Capacitor.getPlatform() === "android") {
      encodedRedirectUri = encodeURIComponent(
        this._config.auth.redirectUriAndroid
      );
    }

    const encodedIdToken = this.idToken;

    let logoutUrl =
      this._config.auth.url +
      this._config.auth.logoutEndpoint +
      "?post_logout_redirect_uri=" +
      encodedRedirectUri +
      "&client_id=" +
      this._config.auth.clientId +
      "&id_token_hint=" +
      encodedIdToken;

    this.removeToken();

    Browser.open({ url: logoutUrl }).then((res) => {});
  }

  public removeToken() {
    this._tokenStorage.clear();
  }
}
